/** Render the subtree as-is, without owning a LiveKit connection. */
type PassthroughPlan = { kind: 'passthrough' };

/**
 * Own the connection for the subtree. `connect` stays false until a token arrives, but the room
 * element is rendered either way: mounting it only once the token lands would remount every preview
 * below it and drop whatever they had already joined.
 */
type RoomPlan = { kind: 'room'; connect: boolean; token: string; serverUrl: string };

export type SharedLiveKitRoomPlan = PassthroughPlan | RoomPlan;

export function resolveSharedLiveKitRoomPlan({
  hasAncestorRoom,
  livekitRoomId,
  token,
  serverUrl,
}: {
  hasAncestorRoom: boolean;
  livekitRoomId?: string;
  token?: string;
  serverUrl?: string;
}): SharedLiveKitRoomPlan {
  // A nested provider must not open a second connection to a room the subtree already has.
  if (hasAncestorRoom || !livekitRoomId) {
    return { kind: 'passthrough' };
  }
  return {
    kind: 'room',
    connect: Boolean(token && serverUrl),
    token: token ?? '',
    serverUrl: serverUrl ?? '',
  };
}

/**
 * Whether a preview should subscribe through an ancestor's room instead of opening its own.
 *
 * Publishing needs a token with a publish grant and its own device state, so a publisher always
 * connects separately even inside a shared room.
 */
export function shouldUseSharedLiveKitRoom(hasSharedRoom: boolean, mode: 'preview' | 'publish' = 'preview'): boolean {
  return hasSharedRoom && mode === 'preview';
}
